import { Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { PlanV2IssueCardPayload, PlanV2IssueDispositionRecord, PlanV2IssueQueue } from '../../utils/plan-v2-issues';
import { encodeWorkItemDetail } from '../../utils/plan-v2-work-item-display';
import { PlanV2WorkItemBodyComponent } from '../plan-v2-work-item-body/plan-v2-work-item-body';
import { PlanV2IssueQueueService } from '../../services/plan-v2-issue-queue.service';
import { PlanV2EmbedContextService } from '../../services/plan-v2-embed-context.service';
import { NotificationService } from '../../services/notification.service';
import type {
  InternalLinkComposeOption,
  TechnicalIssueComposeOption
} from '../../utils/plan-v2-work-item-compose';
import {
  formatInternalLinkRelevanceScore,
  internalLinkDirectionChipClass,
  internalLinkDirectionLabel,
  isTechnicalIssueIncluded,
  setTechnicalIssueParentSelected,
  technicalIssueHasSelectableDetails,
  technicalParentChecked
} from '../../utils/plan-v2-work-item-compose';

type ComposeMode =
  | 'serp_phrase'
  | 'topic_ownership'
  | 'internal_link'
  | 'technical_critical'
  | 'technical_warnings';

@Component({
  selector: 'app-plan-v2-issue-card-actions',
  standalone: true,
  imports: [CommonModule, FormsModule, PlanV2WorkItemBodyComponent],
  templateUrl: './plan-v2-issue-card-actions.html',
  styleUrl: './plan-v2-issue-card-actions.css'
})
export class PlanV2IssueCardActionsComponent {
  @Input({ required: true }) payload!: PlanV2IssueCardPayload;
  @Input() domain = '';
  @Input() compact = false;
  @Output() queueChanged = new EventEmitter<PlanV2IssueQueue>();
  /** True while compose UI is open (parent can hide card body). */
  @Output() composingChange = new EventEmitter<boolean>();

  updating = false;
  animatingOut: 'work_item' | 'dismissed' | null = null;
  composeMode: ComposeMode | null = null;
  technicalOptions: TechnicalIssueComposeOption[] = [];
  internalLinkOptions: InternalLinkComposeOption[] = [];
  draftPayload: PlanV2IssueCardPayload | null = null;

  constructor(
    private readonly issueQueue: PlanV2IssueQueueService,
    private readonly notifications: NotificationService,
    private readonly embedContext: PlanV2EmbedContextService
  ) {}

  get queue(): PlanV2IssueQueue {
    return this.issueQueue.getQueue(this.payload.issueKey);
  }

  get showInIssuesEmbed(): boolean {
    return this.queue === 'issues';
  }

  get isTechnicalCompose(): boolean {
    return this.composeMode === 'technical_critical' || this.composeMode === 'technical_warnings';
  }

  get isInternalLinkCompose(): boolean {
    return this.composeMode === 'internal_link';
  }

  get isTopicOwnershipCompose(): boolean {
    return this.composeMode === 'topic_ownership';
  }

  get isCheckboxCompose(): boolean {
    return this.isTechnicalCompose || this.isInternalLinkCompose;
  }

  get composePreview(): PlanV2IssueCardPayload {
    if (this.composeMode === 'topic_ownership') {
      return this.embedContext.finalizeTopicOwnershipFromCompose(this.payload);
    }
    if (
      this.composeMode === 'technical_critical' ||
      this.composeMode === 'technical_warnings'
    ) {
      return this.embedContext.finalizeTechnicalIssues(
        this.payload,
        this.composeMode,
        this.technicalOptions
      );
    }
    if (this.composeMode === 'internal_link') {
      const items = this.embedContext.finalizeInternalLinks(this.payload, this.internalLinkOptions);
      if (items.length === 1) return items[0];
      const count = items.filter((item) => {
        const detail = (item.actionDetail || '').trim();
        return detail && !detail.includes('Re-open the issue and pick links');
      }).length;
      return {
        ...this.payload,
        changeSummary: `Add ${count} internal links on ${count} source pages.`,
        why: 'Each selected link becomes its own task on the page where you edit copy.',
        actionDetail: encodeWorkItemDetail({
          bullets: items.map((item) => item.changeSummary).slice(0, 12)
        })
      };
    }
    return this.draftPayload ?? this.payload;
  }

  composePreviewRecord(): PlanV2IssueDispositionRecord {
    return {
      ...this.composePreview,
      queue: 'work_item',
      updatedAt: new Date().toISOString()
    };
  }

  sendToWorkItems(event: Event): void {
    event.stopPropagation();
    if (this.updating || this.composeMode) return;
    if (this.embedContext.needsCompose(this.payload)) {
      if (this.payload.issueType === 'topic_ownership') {
        if (!this.embedContext.beginTopicOwnershipCompose()) {
          this.notifications.error('No competitor gaps found for this URL.');
          return;
        }
        this.openCompose('topic_ownership');
        return;
      }
      if (
        this.payload.issueType === 'technical_critical' ||
        this.payload.issueType === 'technical_warnings'
      ) {
        this.technicalOptions = this.embedContext.getTechnicalIssueComposeOptions(this.payload.issueType);
        if (!this.technicalOptions.length) {
          this.notifications.error('No technical issues to add.');
          return;
        }
        this.openCompose(this.payload.issueType);
        return;
      }
      if (this.payload.issueType === 'internal_link') {
        this.internalLinkOptions = this.embedContext.getInternalLinkComposeOptions();
        if (!this.internalLinkOptions.length) {
          this.notifications.error('No internal link suggestions to add.');
          return;
        }
        this.openCompose('internal_link');
        return;
      }
      this.draftPayload = this.embedContext.enrichPayload(this.payload);
      this.openCompose('serp_phrase');
      return;
    }
    this.runTransition(
      'work_item',
      'work_item',
      this.embedContext.hasHost ? this.embedContext.enrichPayload(this.payload) : this.payload
    );
  }

  confirmCompose(event: Event): void {
    event.stopPropagation();
    if (!this.composeMode || this.updating) return;
    let final = this.composePreview;
    if (this.composeMode === 'topic_ownership') {
      if (!this.embedContext.topicOwnershipComposeHasSelection()) {
        this.notifications.error('Select at least one page, tool, or section to include.');
        return;
      }
      final = this.embedContext.finalizeTopicOwnershipFromCompose(this.payload);
    } else if (
      this.composeMode === 'technical_critical' ||
      this.composeMode === 'technical_warnings'
    ) {
      if (!this.technicalOptions.some(isTechnicalIssueIncluded)) {
        this.notifications.error('Select at least one issue to include.');
        return;
      }
      final = this.embedContext.finalizeTechnicalIssues(
        this.payload,
        this.composeMode,
        this.technicalOptions
      );
    } else if (this.composeMode === 'internal_link') {
      const selected = this.internalLinkOptions.filter((opt) => opt.selected);
      if (!selected.length) {
        this.notifications.error('Select at least one link to include.');
        return;
      }
      const payloads = this.embedContext.finalizeInternalLinks(this.payload, this.internalLinkOptions);
      this.runInternalLinkWorkItems(payloads);
      return;
    }
    this.closeCompose();
    this.runTransition('work_item', 'work_item', final);
  }

  cancelCompose(event: Event): void {
    event.stopPropagation();
    this.closeCompose();
  }

  technicalHasSelectableDetails(opt: TechnicalIssueComposeOption): boolean {
    return technicalIssueHasSelectableDetails(opt);
  }

  internalLinkDirectionChip(opt: InternalLinkComposeOption): string {
    return internalLinkDirectionLabel(opt.direction);
  }

  internalLinkDirectionChipClasses(opt: InternalLinkComposeOption): string {
    return internalLinkDirectionChipClass(opt.direction);
  }

  formatInternalLinkScore(score: number): string {
    return formatInternalLinkRelevanceScore(score);
  }

  technicalParentIsChecked(opt: TechnicalIssueComposeOption): boolean {
    return technicalParentChecked(opt);
  }

  onTechnicalParentChange(opt: TechnicalIssueComposeOption, event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;
    setTechnicalIssueParentSelected(opt, checked);
  }

  onTechnicalDetailChange(opt: TechnicalIssueComposeOption): void {
    opt.selected = technicalParentChecked(opt);
  }

  private openCompose(mode: ComposeMode): void {
    if (mode === 'topic_ownership') {
      this.draftPayload = { ...this.payload };
    } else if (mode === 'technical_critical' || mode === 'technical_warnings') {
      this.draftPayload = { ...this.payload };
    } else if (mode === 'internal_link') {
      this.draftPayload = { ...this.payload };
    } else {
      this.draftPayload = this.embedContext.enrichPayload(this.payload);
    }
    this.composeMode = mode;
    this.composingChange.emit(true);
  }

  private closeCompose(): void {
    if (this.composeMode === 'topic_ownership') {
      this.embedContext.endTopicOwnershipCompose();
    }
    this.composeMode = null;
    this.technicalOptions = [];
    this.internalLinkOptions = [];
    this.draftPayload = null;
    this.composingChange.emit(false);
  }

  dismissIssue(event: Event): void {
    event.stopPropagation();
    if (this.updating || this.composeMode) return;
    this.runTransition('dismissed', 'dismissed', this.payload);
  }

  returnToIssues(event: Event): void {
    event.stopPropagation();
    if (this.updating) return;
    this.updating = true;
    this.issueQueue.setDisposition(this.domain, this.payload.issueKey, 'issues').subscribe({
      next: () => {
        this.updating = false;
        this.queueChanged.emit('issues');
      },
      error: () => {
        this.updating = false;
      }
    });
  }

  private runInternalLinkWorkItems(payloads: PlanV2IssueCardPayload[]): void {
    this.updating = true;
    this.animatingOut = 'work_item';
    this.issueQueue.beginCardExit(this.payload.issueKey, 'work_item');
    window.setTimeout(() => {
      this.issueQueue.addInternalLinkWorkItems(this.domain, this.payload.issueKey, payloads).subscribe({
        next: (count) => {
          this.closeCompose();
          this.updating = false;
          this.animatingOut = null;
          this.notifications.success(
            count === 1 ? 'Added to Work Items.' : `Added ${count} work items.`
          );
          this.queueChanged.emit('work_item');
        },
        error: () => {
          this.closeCompose();
          this.updating = false;
          this.animatingOut = null;
          this.issueQueue.clearCardExit(true);
          this.notifications.error('Could not add work items. Try again.');
        }
      });
    }, 240);
  }

  private runTransition(
    queue: 'work_item' | 'dismissed',
    anim: 'work_item' | 'dismissed',
    payload: PlanV2IssueCardPayload,
    options?: { closeComposeWhenDone?: boolean }
  ): void {
    this.updating = true;
    this.animatingOut = anim;
    this.issueQueue.beginCardExit(payload.issueKey, anim);
    window.setTimeout(() => {
      this.issueQueue.setDisposition(this.domain, payload.issueKey, queue, payload).subscribe({
        next: () => {
          if (options?.closeComposeWhenDone) {
            this.closeCompose();
          }
          this.updating = false;
          this.animatingOut = null;
          if (queue === 'work_item') {
            this.notifications.success('Added to Work Items.');
          }
          this.queueChanged.emit(queue);
        },
        error: () => {
          if (options?.closeComposeWhenDone) {
            this.closeCompose();
          }
          this.updating = false;
          this.animatingOut = null;
          this.issueQueue.clearCardExit(true);
          if (queue === 'work_item') {
            this.notifications.error('Could not add work item. Try again.');
          }
        }
      });
    }, 240);
  }
}
